/* eslint-disable no-console */
import {useStore} from '@/store/root-store';
import {observer} from 'mobx-react-lite';
import styled from 'styled-components';
import ReactEcharts from 'echarts-for-react';
import {isNil} from 'lodash';
import {SingleMetrics} from '../SingleMetrics';
import {SkeletonHorizontalLoaderGray} from '@/components/common-components';
import {SidebarTooltip} from '../../../Common/SideBarTooltip/sidebarTooltip';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faArrowDown, faArrowUp, faCheck, faArrowUpRightFromSquare} from '@fortawesome/pro-regular-svg-icons';
import {useState, Dispatch, SetStateAction} from 'react';
import {Tabs, Tooltip} from 'antd';
import {TabPane} from 'rc-tabs';
import {toJS} from 'mobx';

interface ContentScoreProps {
  setViewSuggestionsOption: Dispatch<SetStateAction<boolean>>;
  setLinksDropDownFromSuggestion: Dispatch<SetStateAction<'links' | 'terms'>>;
}

export const ContentScore = observer(({setViewSuggestionsOption, setLinksDropDownFromSuggestion}: ContentScoreProps) => {
  const {
    contentOptimizer: {
      currentPage: {
        isWordCountLoading,
        feTargetKeywords,
        currentEditorState,
        isScoreLoading,
        isFetchingIntLinksSug,
        analytics,
        content: {
          score,
          scoreExtended,
          wordCount,
          suggestedWordCountLimits,
          scoreCounts,
          internalLinkSuggestionsV2,
        },
      },
    }} = useStore('');

  const [activeKey, setActiveKey] = useState('internal');

  const chartOption={
    grid: {
      top: '0px',
      bottom: '0px',
    },
    series: [{
      min: 0,
      max: 100,
      type: 'gauge',
      axisLine: {
        roundCap: false,
        lineStyle: {
          width: 10,
          color: [
            [0.33, '#f44343'],
            [0.66, '#f1aa3e'],
            [1, '#1ea745'],
          ],
        },
      },
      progress: {
        show: false,
      },
      startAngle: 180,
      radius: '90%',
      endAngle: 0,
      pointer: {
        width: 4,
        icon: 'roundRect',
        length: '25%',
        offsetCenter: [0, '-80%'],
        itemStyle: {
          color: '#fff',
        },
      },
      axisTick: {
        show: false,
      },
      splitLine: {
        distance: -13,
        length: 15,
        lineStyle: {
          color: '#24262a',
          width: 6,
        },
      },
      splitNumber: 3,
      axisLabel: {
        show: false,
        color: 'auto',
        distance: 40,
        fontSize: 20,
      },
      detail: {
        show: false,
      },
      data: [{
        value: score ?? 0,
      }],
    }],
  };

  const viewSuggestionHandler = (key: 'links' | 'terms') => {
    setViewSuggestionsOption(true);
    setLinksDropDownFromSuggestion(key);
    setTimeout(() => {
      setViewSuggestionsOption(false);
    }, 1000);
  };
  const scoreLeftPosition = (score?: number) => {
    if (!score || score === 0) return '31%';
    if (score < 100 ) return '27%';
    if (score === 100 ) return '21%';
  };
  const avgTopLeftPosition = (avg?: number, top?: number) => {
    if (!avg && !top) return '25%';
    return '17%';
  };

  const shouldShowTooltip = (min?: number, max?: number) => {
    if (isNil(min) || isNil(max)) {
      return false;
    }

    return true;
  };

  const tooltipMsg = (key: string, min?: number, max?: number, cur?: number) => {
    let msg = '';
    if (cur <= min) msg = `Try to use more ${key}. Ideally you should use ${min}-${max} ${key}`;
    if (cur >= max) msg = `Try to use less ${key}. Ideally you should use ${min}-${max} ${key}`;
    if (cur >= min && cur <= max) msg = `Good job. You're in the optimal range.`;
    return msg;
  };

  const setHeadingsTooltip = (h1value: number, h1Min: number, h1Max: number, h2value: number, h2Min: number, h2Max: number, h3value: number, h3Min: number, h3Max: number) => {
    return <StyleHeadingsTooltipTitle>
      <p style={{display: 'flex', gap: '20px'}}>
        <span>H1 headings</span>
        <span>
          <FontAwesomeIcon icon={(h1value === 0) ? faArrowUp : (h1value === 1) ? faCheck : faArrowDown} color={(h1value >= 2) ? 'red' : 'green'}/>
          {' '}{h1value}/{h1Min}-{h1Max}
        </span>
      </p>
      <p style={{display: 'flex', gap: '20px'}}>
        <span>H2 headings</span>
        <span>
          <FontAwesomeIcon icon={(h2value === 0) ? faArrowUp : (h2value === 1) ? faCheck : faArrowDown} color={(h2value >= 2) ? 'red' : 'green'}/>
          {' '}{h2value}/{h2Min}-{h2Max}
        </span>
      </p>
      <p style={{display: 'flex', gap: '20px', marginBottom: '0px'}}>
        <span>H3 headings</span>
        <span>
          <FontAwesomeIcon icon={(h3value === 0) ? faArrowUp : (h3value === 1) ? faCheck : faArrowDown} color={(h3value >= 2) ? 'red' : 'green'}/>
          {' '}{h3value}/{h3Min}-{h3Max}
        </span>
      </p>
    </StyleHeadingsTooltipTitle>;
  };
  const onChangeTab = key => {
    setActiveKey(key);
  };

  const openText = scrollToText => {
    const anchorTag = document.querySelector('a[href="' + scrollToText + '"]') as HTMLElement;
    if (anchorTag) {
      anchorTag?.classList.add('animateAnchor');
      anchorTag?.scrollIntoView({behavior: 'smooth', block: 'start'});
      setTimeout(() => {
        anchorTag?.classList.remove('animateAnchor');
      }, 3000);
    }
  };


  const setLinksTooltip = (internalLinksData, externalLinksData) => {
    return <StyleHeadingsTooltipTitle>
      <StyledLinksTabs
        activeKey={activeKey}
        defaultActiveKey={'internal'}
        onChange={key => onChangeTab(key)}
      >
        <TabsStyled active={activeKey === 'internal'} tab='Internal Links' key='internal'>
          <StyledRecommender>Recommended:
            <StyledRecomentedValue> {internalLinksData.internalLinksMin} - {internalLinksData.internalLinksMax}
              <StyledSuggestionLink
                showAsLink={!isFetchingIntLinksSug && internalLinkSuggestionsV2?.allLinks?.length > 0 ? true : false}
                href='javascript:void(0)'
                onClick={!isFetchingIntLinksSug && internalLinkSuggestionsV2?.allLinks?.length > 0 ? ()=> viewSuggestionHandler('links') : ()=> {}}>
                {!isFetchingIntLinksSug && internalLinkSuggestionsV2?.allLinks?.length > 0 ? 'View suggestions' : 'No suggestions'}
              </StyledSuggestionLink>
            </StyledRecomentedValue>
          </StyledRecommender>
          <StyledCurrentValue>Current value:
            <span>{internalLinksData.internalLinksLength}</span>
            <StyledFontAwesomeIcon
              icon={(internalLinksData.internalLinksLength < internalLinksData.internalLinksMin) ?
                faArrowUp : (internalLinksData.internalLinksLength > internalLinksData.internalLinksMax) ?
                  faArrowDown : faCheck}
              color={(internalLinksData.internalLinksLength < internalLinksData.internalLinksMin) ?
                'red' : (internalLinksData.internalLinksLength > internalLinksData.internalLinksMax) ?
                  'red' : 'green'}/>
          </StyledCurrentValue>
          <StyledLinksDiv>
            {internalLinksData?.internalLinks?.map((item, index) => <li key={index}><Tooltip title='Show link in content'><StyledLinkItem onClick={()=> openText(item)}>{item}</StyledLinkItem></Tooltip><Tooltip title='Open in a new tab'>
              <FontAwesomeIcon className='linkopen' onClick={()=> window.open(item, '_blank')} icon={faArrowUpRightFromSquare} /></Tooltip></li>)}
          </StyledLinksDiv>
        </TabsStyled>
        <TabsStyled active={activeKey === 'external'} tab='External Links' key='external'>
          <StyledRecommender>Recommended:
            <StyledRecomentedValue> {externalLinksData.externalLinksMin} - {externalLinksData.externalLinksMax}
              <StyledSuggestionLink
                showAsLink={!isFetchingIntLinksSug && analytics?.commonCitations?.length > 0 ? true : false}
                href='javascript:void(0)'
                onClick={!isFetchingIntLinksSug && analytics?.commonCitations?.length > 0 ? ()=> viewSuggestionHandler('terms') : ()=> {}}>
                {!isFetchingIntLinksSug && analytics?.commonCitations?.length > 0 ? 'View suggestions' : 'No suggestions'}
              </StyledSuggestionLink>
            </StyledRecomentedValue>
          </StyledRecommender>
          <StyledCurrentValue>Current value:
            <span>{externalLinksData.externalLinksLength}</span>
            <StyledFontAwesomeIcon
              icon={(externalLinksData.externalLinksLength < externalLinksData.externalLinksMin) ?
                faArrowUp : (externalLinksData.externalLinksLength > externalLinksData.externalLinksMax) ?
                  faArrowDown : faCheck}
              color={(externalLinksData.externalLinksLength < externalLinksData.externalLinksMin) ?
                'red' : (externalLinksData.externalLinksLength > externalLinksData.externalLinksMax) ?
                  'red' : 'green'}/>
          </StyledCurrentValue>
          <StyledLinksDiv>
            {externalLinksData?.externalLinks?.map((item, index) => <li key={index}><Tooltip title='Show link in content'><StyledLinkItem onClick={()=> openText(item)}>{item}</StyledLinkItem></Tooltip>
              <Tooltip title='Open in a new tab'><FontAwesomeIcon className='linkopen' onClick={()=> window.open(item, '_blank')} icon={faArrowUpRightFromSquare} /></Tooltip></li>)}
          </StyledLinksDiv>
        </TabsStyled>
      </StyledLinksTabs>
    </StyleHeadingsTooltipTitle>;
  };

  const countsSetup = [{
    label: 'Word Count:',
    value: wordCount,
    min: suggestedWordCountLimits?.lower,
    max: suggestedWordCountLimits?.upper,
    tooltipLabel: tooltipMsg('words', suggestedWordCountLimits?.lower, suggestedWordCountLimits?.upper, wordCount),
    showTooltip: shouldShowTooltip(suggestedWordCountLimits?.lower, suggestedWordCountLimits?.upper),
    loading: isWordCountLoading,
  },
  {
    label: 'Paragraphs:',
    value: scoreCounts?.paragraphs,
    min: scoreCounts?.paragraphsMin,
    max: scoreCounts?.paragraphsMax,
    tooltipLabel: tooltipMsg('paragraphs', scoreCounts?.paragraphsMin, scoreCounts?.paragraphsMax, scoreCounts?.paragraphs),
    showTooltip: shouldShowTooltip(scoreCounts?.paragraphsMin, scoreCounts?.paragraphsMax),
    loading: isWordCountLoading,
  },
  {
    label: 'Headings:',
    value: scoreCounts?.headings,
    min: scoreCounts?.headingsMin,
    max: scoreCounts?.headingsMax,
    // tooltipLabel: tooltipMsg('headings', scoreCounts?.headingsMin, scoreCounts?.headingsMax, scoreCounts?.headings),
    tooltipLabel: setHeadingsTooltip(scoreCounts?.h1, scoreCounts?.h1Min, scoreCounts?.h1Max, scoreCounts?.h2, scoreCounts?.h2Min, scoreCounts?.h2Max, scoreCounts?.h3, scoreCounts?.h3Min, scoreCounts?.h3Max),
    showTooltip: false,
    loading: isWordCountLoading,
    headingData: {
      h1: scoreCounts?.h1,
      h2: scoreCounts?.h2,
      h3: scoreCounts?.h3,
      h1Min: scoreCounts?.h1Min,
      h1Max: scoreCounts?.h1Max,
      h2Min: scoreCounts?.h2Min,
      h2Max: scoreCounts?.h2Max,
      h3Min: scoreCounts?.h3Min,
      h3Max: scoreCounts?.h3Max,
    },
  },
  {
    label: 'Links:',
    value: 30,
    min: 2,
    max: 12,
    showTooltip: shouldShowTooltip(scoreCounts?.paragraphsMin, scoreCounts?.paragraphsMax),
    loading: isWordCountLoading,
    tooltipLabel: setLinksTooltip(
      {
        internalLinks: toJS(scoreCounts?.internalLinks),
        internalLinksLength: !scoreCounts?.internalLinks ? '-' : scoreCounts?.internalLinks?.length,
        internalLinksMin: scoreCounts?.internalLinksMin,
        internalLinksMax: scoreCounts?.internalLinksMax,
      },
      {
        externalLinks: toJS(scoreCounts?.externalLinks),
        externalLinksLength: !scoreCounts?.externalLinks ? '-' : scoreCounts?.externalLinks?.length,
        externalLinksMin: scoreCounts?.externalLinksMin,
        externalLinksMax: scoreCounts?.externalLinksMax,
      },
    ),
    linksData: {
      internalLinks: !scoreCounts?.internalLinks ? '-' : scoreCounts?.internalLinks?.length,
      externalLinks: !scoreCounts?.externalLinks ? '-' : scoreCounts?.externalLinks?.length,
      internalLinksMin: scoreCounts?.internalLinksMin,
      internalLinksMax: scoreCounts?.internalLinksMax,
      externalLinksMax: scoreCounts?.externalLinksMax,
      externalLinksMin: scoreCounts?.externalLinksMin,
    },
  },
  // {
  //   label: 'Images:',
  //   value: scoreCounts?.images,
  //   min: scoreCounts?.imagesMin,
  //   max: scoreCounts?.imagesMax,
  //   tooltipLabel: tooltipMsg('images', scoreCounts?.imagesMin, scoreCounts?.imagesMax, scoreCounts?.images),
  //   showTooltip: shouldShowTooltip(scoreCounts?.imagesMin, scoreCounts?.imagesMax),
  //   loading: isWordCountLoading,
  // },
  ];

  const contentProgressMessage = value => {
    if ((feTargetKeywords?.length == 0 || !currentEditorState?.getCurrentContent().hasText()) && value == 0) {
      return <>
        {!isScoreLoading ? <div style={{fontSize: 12}}>
      Add Target Keywords and Start Writing to see how your content ranks
        </div> :
          <SkeletonHorizontalLoaderGray height='24px' skeletonHeight='10px' width='100%' align='flex-start'/>}</>;
    }
    if (value < 26) {
      return <>
        {!isScoreLoading ? <div style={{fontSize: 12}}>
        Add Focus Terms and length to improve your ranking potential.
        </div> :
          <SkeletonHorizontalLoaderGray height='24px' skeletonHeight='10px' width='100%' align='flex-start'/>}</>;
    }
    if (value > 25 && value < 51) {
      return <>
        {!isScoreLoading ? <div style={{fontSize: 12}}>
        Getting there! But there is still lots of opportunity to improve.
        </div> :
          <SkeletonHorizontalLoaderGray height='24px' skeletonHeight='10px' width='100%' align='flex-start'/>}
      </>;
    }
    if (value > 50 && value < 76) {
      return <>
        {!isScoreLoading ? <div style={{fontSize: 12}}>
        Good work! Your content has an average chance of ranking.
        </div> :
          <SkeletonHorizontalLoaderGray height='24px' skeletonHeight='10px' width='100%' align='flex-start'/>}
      </>;
    }
    if (value > 75 && value <= 100) {
      return <>
        {!isScoreLoading ? <div>
        Great work! Your content has the best chance of ranking.
        </div> :
          <SkeletonHorizontalLoaderGray height='24px' skeletonHeight='10px' width='100%' align='flex-start'/>}
      </>;
    }
    return '';
  };


  return <Container>
    <div style={{display: 'flex'}}>
      <div style={{position: 'relative', width: 140, height: 65, overflow: 'hidden'}}>
        <ReactEcharts
          style={{width: 140, height: 140}}
          option={chartOption}
        />

        <SidebarTooltip title={contentProgressMessage(score)}>
          <div style={{position: 'absolute', left: scoreLeftPosition(score), top: '30%', cursor: 'default'}}><span style={{fontSize: 24}}>{score ?? 0}</span><span style={{color: '#A3A4A4', marginLeft: 5}}>/100</span></div>
        </SidebarTooltip>
        <div style={{position: 'absolute', left: avgTopLeftPosition(scoreExtended?.avg, scoreExtended?.top), bottom: '-5%', fontSize: 12}}>
          <SidebarTooltip placement='bottom' title='Average Content Score among competitorsAverage Content Score among competitors'>
            <span style={{cursor: 'pointer', color: '#A3A4A4', marginRight: 3}}>{`Avg: ${scoreExtended?.avg ?? '-'}`}</span>
          </SidebarTooltip>
          <SidebarTooltip placement='bottom' title='Top Content Score among competitors'>
            <span style={{cursor: 'pointer', color: '#A3A4A4'}}>{`Top: ${scoreExtended?.top ?? '-'}`}</span>
          </SidebarTooltip>
        </div>

      </div>
      <RightSection>
        {countsSetup.map((item, idx) => {
          return <SingleMetrics key={idx} label={item.label} value={item.value} min={item.min} max={item.max} tooltipLabel={item.tooltipLabel} showTooltip={item.showTooltip} loading={item.loading} headingData={item.headingData} linksData={item.linksData}/>;
        })}
      </RightSection>

    </div>


  </Container>;
});

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;
const RightSection = styled.div`

`;
const StyleHeadingsTooltipTitle = styled.div``;
const StyledLinksTabs = styled(Tabs)`
color: #4E5156;
font-size: 12px;
font-weight: 400;
.ant-tabs-nav {
  margin: 0 0 12px 0;
 
  .ant-tabs-nav-wrap {
    .ant-tabs-nav-list{
      .ant-tabs-tab{
        padding: 4px 0px;
        &:hover{
          color: #121212;
        }
      }
      .ant-tabs-tab-active{
        .ant-tabs-tab-btn {
          color: #121212;

        }
      }
      .ant-tabs-ink-bar{
        background: #F1AA3E !important;
        height: 5px;
        border-radius: 2px 2px 0px 0px;
      }
    }
  }
}
`;

const TabsStyled = styled(TabPane)``;
const StyledLinksDiv = styled.ul`
max-height: 200px;
overflow-y: auto;
margin-left: -15px;
.linkopen{
  margin-left: 5px !important;
  cursor: pointer;
  &:hover{
    color: #2D6CCA !important;
  }
}
::-webkit-scrollbar {
  width: 4px;
}
::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}
::-webkit-scrollbar-thumb {
  background: #3E4144;
  border-radius: 10px;
}
::-webkit-scrollbar-thumb:hover {
  background: #555;
}
`;

const StyledRecommender = styled.div`
color: #4E5156;
font-family: 'Inter', sans-serif;
font-size: 12px;
font-weight: 400;
`;
const StyledRecomentedValue = styled.span`
color:  #121212;
font-family: 'Inter', sans-serif;
font-size: 12px;
font-weight: 600;
`;
const StyledSuggestionLink = styled.a<{showAsLink}>`
color: ${p=> p.showAsLink ? '#2D6CCA' : '#A3A4A4'};
font-family: 'Inter', sans-serif;
font-size: 12px;
font-style: normal;
font-weight: 400;
text-decoration-line: none;
margin-left: 10px;
cursor: ${p=> p.showAsLink ? 'pointer' : 'default'};
&:hover{
  color: ${p=> p.showAsLink ? '#5487d4' : '#A3A4A4'};
  text-decoration-line: ${p=> p.showAsLink ? 'underline' : 'none'};
}
`;
const StyledCurrentValue= styled.p`
color: #4E5156;
font-family: 'Inter', sans-serif;
font-size: 12px;
font-weight: 400;
margin-bottom: 2px;
span{
  color:#121212;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  margin-left: 4px;
}
`;
const StyledLinkItem = styled.span`
color: #000;
font-family: 'Inter', sans-serif;
font-size: 12px;
font-style: normal;
font-weight: 400;
line-height: 18px;
margin-bottom: 2px;
cursor: pointer !important;
&:hover{
  text-decoration: underline;
}
`;
const StyledFontAwesomeIcon = styled(FontAwesomeIcon)`
margin-left: 6px;
height: 10px;
padding-bottom:1px;
`;

// const StyledAnchor = styled.a`
//   text-decoration: none;
//   transition: text-decoration 0.2s;

//   &:hover {
//     text-decoration: underline;
//   }
// `;
